<template lang="pug">
    .order-notifications(v-loading="loading")
        el-alert(
            v-if="!whatsappAccount"
            type="info"
            :closable="false"
            show-icon
            :title="$t('Connect a WhatsApp number to send order updates via WhatsApp.')"
        )
        .notification-events(v-else)
            //- "Could not load" is not "no templates": the saved choices below cannot be checked.
            el-alert.notification-events__load-error(
                v-if="templatesError"
                type="warning"
                :closable="false"
                show-icon
                :title="$t('The templates could not be loaded from Meta. Reconnect the WhatsApp number.')"
                :description="templatesError"
            )
            .notification-event(v-for="entry in eventEntries" :key="entry.event")
                .notification-event__head
                    span.notification-event__name {{ entry.label }}
                    el-switch(v-model="entry.rule.enabled" :disabled="disabled" :active-text="$t('Send via WhatsApp')")
                el-form.notification-event__form(v-if="entry.rule.enabled" :disabled="disabled" label-position="top")
                    el-form-item(:label="entry.event === 'order.shipped' ? $t('Template with tracking') : $t('Template')")
                        .notification-event__template
                            el-select.notification-event__template-select(
                                :value="entry.rule.template_name"
                                filterable
                                :placeholder="$t('Choose an approved template')"
                                @input="value => setTemplate(entry, value)"
                            )
                                el-option(
                                    v-for="template in utilityTemplates"
                                    :key="template.name"
                                    :label="template.name"
                                    :value="template.name"
                                )
                            el-button.notification-event__create(type="text" icon="el-icon-fa-plus fas" @click="$emit('manage-templates', templatePreset(entry))") {{ $t('Create template') }}
                        span.help {{ $t('Only approved utility templates can deliver order updates.') }}
                        span.help.is-warning(v-if="templateUnavailable(entry.rule.template_name)") {{ $t('Meta does not list this template as an approved utility template, so it is not sent.') }}
                    template(v-if="selectedTemplate(entry)")
                        //- The full card (header / body / footer / buttons) via the SHARED
                        //- WhatsAppCardPreview renderer, in a tinted bubble panel (mirrors the
                        //- template manager) so the owner sees exactly what the customer receives.
                        whats-app-card-preview.notification-event__preview(v-if="selectedTemplate(entry).body" framed v-bind="previewCard(selectedTemplate(entry))")
                        //- The template knows its own named placeholders - everything fills
                        //- itself from the order, nothing to configure.
                        .notification-event__autofill(v-if="autoFilledLabels(entry).length")
                            i.el-icon-fa-magic.fas
                            span  {{ $t('Filled automatically from the order:') }} {{ autoFilledLabels(entry).join(' · ') }}
                        el-alert.notification-event__alert(
                            v-else-if="templateNeedsManualVariables(entry)"
                            type="warning"
                            :closable="false"
                            show-icon
                            :title="$t('This template was not built here, so its variables cannot be filled from the order. Create one with named placeholders instead.')"
                        )
                            el-button(type="text" @click="$emit('manage-templates', templatePreset(entry))") {{ $t('Create template') }}
                    //- The shipped event can use a second template for orders WITHOUT a tracking
                    //- number, so a tracking button / number is never sent empty.
                    el-form-item.notification-event__second-template(v-if="entry.event === 'order.shipped'" :label="$t('Template without tracking')")
                        .notification-event__template
                            el-select.notification-event__template-select(
                                :value="entry.rule.template_name_without_tracking"
                                filterable
                                clearable
                                :placeholder="$t('Optional, uses the template above if empty')"
                                @input="value => entry.rule.template_name_without_tracking = value"
                            )
                                el-option(
                                    v-for="template in utilityTemplates"
                                    :key="template.name"
                                    :label="template.name"
                                    :value="template.name"
                                )
                            el-button.notification-event__create(type="text" icon="el-icon-fa-plus fas" @click="$emit('manage-templates', templatePreset(entry, true))") {{ $t('Create template') }}
                        //- Same shared card preview for the without-tracking template.
                        whats-app-card-preview.notification-event__preview(v-if="templateByName(entry.rule.template_name_without_tracking)" framed v-bind="previewCard(templateByName(entry.rule.template_name_without_tracking))")
                        span.help {{ $t('Sent when the order has no tracking number yet. Leave empty to always use the template above.') }}
                        span.help.is-warning(v-if="templateUnavailable(entry.rule.template_name_without_tracking)") {{ $t('Meta does not list this template as an approved utility template, so it is not sent.') }}
                    el-form-item.notification-event__email(:label="$t('Email')")
                        el-radio-group(:value="emailMode(entry)" @input="mode => setEmailMode(entry, mode)")
                            el-radio(label="always") {{ $t('Always send the email too') }}
                            el-radio(label="fallback") {{ $t('Only if WhatsApp is not delivered') }}
                        .notification-event__fallback(v-if="emailMode(entry) === 'fallback'")
                            span.notification-event__fallback-lead {{ $t('Wait') }}
                            el-input-number(v-model="entry.rule.fallback_hours" :min="1" :max="72" :step="1" size="mini" controls-position="right")
                            span.notification-event__fallback-unit {{ $t('hours, then send the email unless WhatsApp reports it delivered or read.') }}
                        span.help(v-else) {{ $t('The email is always sent as well - WhatsApp is an added touch, never the only channel.') }}
</template>

<i18n>
{
    "de": {
        "Connect a WhatsApp number to send order updates via WhatsApp.": "Verbinde eine WhatsApp-Nummer, um Bestellupdates per WhatsApp zu versenden.",
        "Send via WhatsApp": "Per WhatsApp senden",
        "Template": "Vorlage",
        "Choose an approved template": "Freigegebene Vorlage auswählen",
        "Only approved utility templates can deliver order updates.": "Nur freigegebene Utility-Vorlagen können Bestellupdates zustellen.",
        "Create template": "Vorlage erstellen",
        "Template with tracking": "Vorlage mit Sendungsverfolgung",
        "Template without tracking": "Vorlage ohne Sendungsverfolgung",
        "Optional, uses the template above if empty": "Optional, verwendet sonst die obige Vorlage",
        "Sent when the order has no tracking number yet. Leave empty to always use the template above.": "Wird gesendet, wenn die Bestellung noch keine Sendungsnummer hat. Leer lassen, um immer die obige Vorlage zu verwenden.",
        "Filled automatically from the order:": "Wird automatisch aus der Bestellung befüllt:",
        "This template was not built here, so its variables cannot be filled from the order. Create one with named placeholders instead.": "Diese Vorlage wurde nicht hier erstellt, daher können ihre Variablen nicht aus der Bestellung befüllt werden. Erstelle stattdessen eine mit benannten Platzhaltern.",
        "Link button": "Link-Schaltfläche",
        "The templates could not be loaded from Meta. Reconnect the WhatsApp number.": "Die Vorlagen konnten nicht von Meta geladen werden. Verbinde die WhatsApp-Nummer neu.",
        "Meta does not list this template as an approved utility template, so it is not sent.": "Meta führt diese Vorlage nicht als freigegebene Utility-Vorlage, daher wird sie nicht gesendet.",
        "Customer name": "Kundenname",
        "Member name": "Mitgliedsname",
        "Recipient name": "Name des Empfängers",
        "Order number": "Bestellnummer",
        "Order date": "Bestelldatum",
        "Order status": "Bestellstatus",
        "Tracking number": "Sendungsnummer",
        "Amount": "Betrag",
        "Email": "E-Mail",
        "Always send the email too": "E-Mail immer zusätzlich senden",
        "Only if WhatsApp is not delivered": "Nur wenn WhatsApp nicht zugestellt wird",
        "The email is always sent as well - WhatsApp is an added touch, never the only channel.": "Die E-Mail wird immer zusätzlich gesendet - WhatsApp ist eine Ergänzung, niemals der einzige Kanal.",
        "Wait": "Warte",
        "hours, then send the email unless WhatsApp reports it delivered or read.": "Stunden, dann sende die E-Mail, sofern WhatsApp sie nicht als zugestellt oder gelesen meldet.",
        "Order confirmation": "Bestellbestätigung",
        "Status changed": "Statusänderung",
        "Order updated": "Bestellung aktualisiert",
        "Shipped": "Versandbestätigung",
        "Refunded / credit note": "Erstattung / Gutschrift"
    }
}
</i18n>

<script>
import _ from 'lodash'
import { mapState } from 'pinia'
import { useSetupStore } from '@/stores/setup'

import ChannelAccountApi from '@/extensions/communication/store/api/channelAccount'
import ConversationApi from '@/extensions/communication/store/api/conversation'
import OrderNotificationApi from '@/extensions/communication/store/api/orderNotification'
import { whatsappPlaceholderByKey } from '@/extensions/communication/utils/whatsappPlaceholders'
import { orderNotificationTemplatePreset } from '@/extensions/communication/utils/orderNotificationTemplatePresets'
import { templateCardPreview } from '@/extensions/communication/utils/whatsappTemplateCard'
import WhatsAppCardPreview from '@/extensions/communication/views/Partials/WhatsAppCardPreview'

// The per-event channel matrix of the order-notifications addon: for each order
// event the shop picks ONE approved WhatsApp UTILITY template - that is the whole
// configuration. Templates carry their own named placeholders (captured when they
// were authored), so the variables fill themselves from the order at send time;
// the email always remains the guaranteed channel. Edited in place and flushed by
// the settings shell's Save (List.vue saveTabSettings), no per-section button.
export default {
    name: 'CommunicationOrderNotifications',
    // Explicit kebab key so the <whats-app-card-preview> tag resolves.
    components: { 'whats-app-card-preview': WhatsAppCardPreview },
    props: {
        cmsId: { type: [Number, String], required: true },
        // Without ex:comm:accounts update the rules are shown, not edited.
        disabled: { type: Boolean, default: false }
    },
    data() {
        return {
            loading: false,
            whatsappAccount: null,
            templates: [],
            templatesError: null,
            eventEntries: this.emptyEventEntries()
        }
    },
    computed: {
        ...mapState(useSetupStore, ['activeCms']),
        // The storefront declares an order link, so a template can carry a "View order" button
        // (the same contract the LinkPicker offers its order option on).
        hasOrderLink() {
            return (_.get(this.activeCms, 'config.template.links') || []).some(link => link.key === 'order')
        },
        utilityTemplates() {
            return this.templates.filter(template => String(template.category || '').toUpperCase() === 'UTILITY')
        },
        // The named template placeholders (key -> { label, ... }) - the same single
        // source the template create editor and the composer's picker use.
        placeholderByKey() {
            const enabledExtensions = useSetupStore().enabledExtensions
            return whatsappPlaceholderByKey({
                t: this.$t.bind(this),
                hasShop: enabledExtensions.includes('shop'),
                hasMembers: enabledExtensions.includes('shop') || enabledExtensions.includes('restricted-area')
            })
        }
    },
    mounted() {
        this.load()
    },
    methods: {
        // Only the events that actually fire today - the enum has more for the future.
        emptyEventEntries() {
            const events = [
                { event: 'order.placed', label: this.$t('Order confirmation') },
                { event: 'order.status_changed', label: this.$t('Status changed') },
                { event: 'order.updated', label: this.$t('Order updated') },
                { event: 'order.shipped', label: this.$t('Shipped') },
                { event: 'order.refunded', label: this.$t('Refunded / credit note') }
            ]

            return events.map(entry => ({ ...entry, rule: this.emptyRule(entry.event) }))
        },
        emptyRule(event) {
            return {
                id: null,
                event,
                channels: ['whatsapp'],
                template_name: null,
                template_name_without_tracking: null,
                fallback_hours: 0,
                enabled: false
            }
        },
        // The CMS-level WhatsApp number and its templates. Also called by the page when the
        // number or its templates change, so it never touches the (unsaved) rules.
        async loadWhatsApp() {
            const accounts = await ChannelAccountApi.fetchList(this.cmsId)
            this.whatsappAccount = (accounts || []).find(account => {
                return account.channel === 'whatsapp'
                    && account.is_active !== false
                    && !(account.pointsOfSale || []).length
            }) || null

            this.templates = []
            this.templatesError = null
            if (!this.whatsappAccount) return

            // Kept apart from the rules: a template list Meta refuses must not hide the
            // shop's saved choices, it only means they cannot be checked.
            try {
                this.templates = await ConversationApi.fetchWhatsAppTemplates(this.whatsappAccount.id)
            } catch (error) {
                this.templatesError = _.get(error, 'graphQLErrors.0.message') || this.$t('Something went wrong!')
            }
        },
        // A saved template Meta does not (or no longer) list as approved utility. Unknown
        // while the list could not be loaded, so no warning then.
        templateUnavailable(name) {
            return !!name && !this.templatesError && !this.templateByName(name)
        },
        async load() {
            this.loading = true
            try {
                await this.loadWhatsApp()

                const rules = await OrderNotificationApi.fetchList(this.cmsId)
                this.eventEntries.forEach(entry => {
                    const existing = (rules || []).find(rule => rule.event === entry.event)
                    if (!existing) return
                    entry.rule = {
                        id: existing.id,
                        event: existing.event,
                        channels: existing.channels || ['whatsapp'],
                        template_name: existing.template_name || null,
                        template_name_without_tracking: existing.template_name_without_tracking || null,
                        fallback_hours: existing.fallback_hours || 0,
                        enabled: !!existing.enabled
                    }
                })
            } catch (error) {
                this.$notify({
                    title: this.$t('Error'),
                    message: this.$t('The order notification settings could not be loaded.'),
                    type: 'error',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                })
            } finally {
                this.loading = false
            }
        },
        selectedTemplate(entry) {
            return this.templateByName(entry.rule.template_name)
        },
        templateByName(name) {
            return name ? (this.utilityTemplates.find(template => template.name === name) || null) : null
        },
        // The full card (header / body / footer / buttons) with the template's named
        // placeholders as pills — the shared mapping, so the ticket desk's support
        // template preview and this one can never drift apart.
        previewCard(template) {
            return templateCardPreview(template, this.placeholderByKey)
        },
        // Human labels of everything the chosen template fills by itself: its named
        // body/header placeholders plus its dynamic link buttons.
        autoFilledLabels(entry) {
            const template = this.selectedTemplate(entry)
            if (!template) return []

            const keys = [
                ...(template.header_variable_keys || []),
                ...(template.variable_keys || [])
            ].filter(Boolean)

            const labels = keys.map(key => {
                const placeholder = this.placeholderByKey[key]
                return placeholder ? placeholder.label : key
            })

            const dynamicButtons = (template.button_variable_keys || []).filter(Boolean)
            dynamicButtons.forEach(() => labels.push(this.$t('Link button')))

            return labels
        },
        // A foreign template (authored outside the manager) has variables but no
        // stored named keys - it cannot be filled automatically.
        templateNeedsManualVariables(entry) {
            const template = this.selectedTemplate(entry)
            if (!template) return false

            return (template.variable_count || 0) > 0 && !(template.variable_keys || []).filter(Boolean).length
        },
        // The recommended template for this event, opened prefilled by "Create template".
        templatePreset(entry, withoutTracking = false) {
            const defaultLanguage = (_.get(this.activeCms, 'languages') || []).find(language => language.default)
            return orderNotificationTemplatePreset(entry.event, {
                language: defaultLanguage ? defaultLanguage.code : null,
                withoutTracking,
                withOrderLink: this.hasOrderLink
            })
        },
        setTemplate(entry, templateName) {
            entry.rule.template_name = templateName
        },
        // Email behaviour is stored as fallback_hours (0 = always send, >0 = delayed fallback).
        // The radio hides that: "always" is 0, "fallback" reveals the hours (defaulting to a
        // sensible 4 the first time, so switching never lands on an invalid 0-hour fallback).
        emailMode(entry) {
            return entry.rule.fallback_hours > 0 ? 'fallback' : 'always'
        },
        setEmailMode(entry, mode) {
            entry.rule.fallback_hours = mode === 'fallback' ? (entry.rule.fallback_hours || 4) : 0
        },
        // Flushed by List.vue's saveTabSettings (the settings shell's Save).
        async save() {
            const touched = this.eventEntries.filter(entry => entry.rule.enabled || entry.rule.id)
            await Promise.all(touched.map(entry => OrderNotificationApi.save(this.cmsId, entry.rule)))
        }
    }
}
</script>

<style lang="sass" scoped>
.order-notifications
    .help
        display: block
        line-height: 1.4
        font-size: .75rem
        color: #909399
        margin-top: 4px
    .notification-events
        display: flex
        flex-direction: column
        gap: 10px
    .notification-event
        padding: 14px 16px
        background: #fff
        border: 1px solid #ebeef5
        border-radius: 8px
        .notification-event__head
            display: flex
            align-items: center
            justify-content: space-between
            gap: 8px
        .notification-event__name
            font-weight: 600
        .notification-event__form
            margin-top: 14px
            padding-top: 14px
            border-top: 1px solid #f2f4f7
            // Give the stacked form-items real breathing room (Element's default is cramped here).
            ::v-deep .el-form-item
                margin-bottom: 18px
                &:last-child
                    margin-bottom: 0
        // Template select + inline "Create template" affordance on one row.
        .notification-event__template
            display: flex
            align-items: center
            gap: 10px
            .notification-event__template-select
                flex: 1
                min-width: 0
            .notification-event__create
                flex-shrink: 0
                padding: 0
        // The tinted panel + label come from the shared WhatsAppCardPreview (framed); we only
        // own the spacing between events here.
        .notification-event__preview
            margin: 4px 0 10px
        .notification-event__autofill
            display: flex
            align-items: baseline
            gap: 5px
            font-size: .78rem
            line-height: 1.5
            color: #909399
            i
                color: #409EFF
        .notification-event__alert
            ::v-deep .el-alert__content
                display: flex
                flex-direction: column
                align-items: flex-start
                gap: 2px
            ::v-deep .el-button
                padding: 0
        // The shipped event's second (without-tracking) template select — space it off the
        // autofill hint of the first, like the email block.
        .notification-event__second-template
            margin-top: 1.25rem
        // Email behaviour: the radio choice, then a compact inline "wait N hours" row.
        .notification-event__email
            margin-top: 1.25rem
            ::v-deep .el-radio-group
                display: flex
                flex-direction: column
                gap: 10px
                margin-top: 4px
            ::v-deep .el-radio
                margin-right: 0
        .notification-event__fallback
            display: flex
            align-items: center
            flex-wrap: wrap
            gap: 8px
            margin-top: 12px
            padding: 12px
            background: #f7f9fb
            border-radius: 8px
            .notification-event__fallback-lead
                font-size: .82rem
                color: #606266
            .notification-event__fallback-unit
                flex: 1
                min-width: 140px
                font-size: .78rem
                line-height: 1.4
                color: #909399
</style>
